import { Link, useNavigate } from "react-router-dom";
import { Mail } from "lucide-react";
import { t } from "@/lib/i18n";
import { useSite } from "@/lib/site";

export const MarketingNav = ({ active }) => {
  const navigate = useNavigate();
  const { s, brand } = useSite("shell");
  const bn = brand.app_name || "Agentic Inbox";
  const link = (to, label, id) => (
    <Link to={to} data-testid={id}
      className={`transition-colors ${active === to ? "text-[#1d1d1f] font-semibold" : "hover:text-[#1d1d1f]"}`}>{label}</Link>
  );
  return (
    <header className="demo-nav fixed top-0 inset-x-0 z-50 h-16 max-md:h-auto">
      <div className="max-w-6xl mx-auto h-16 px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="mkt-brand">
          <div className="w-7 h-7 rounded-lg bg-[#0071E3] flex items-center justify-center text-white overflow-hidden">
            {brand.logo_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${brand.logo_url}`} alt={bn} className="w-7 h-7 object-cover" data-testid="mkt-brand-logo" /> : <Mail size={14} />}
          </div>
          <span className="demo-heading font-semibold text-[15px] tracking-tight text-[#1d1d1f]">{bn}</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-[13px] text-[#1d1d1f]/70">
          {link("/features", s("nav_features", "Features"), "mkt-nav-features")}
          {link("/pricing", s("nav_pricing", "Pricing"), "mkt-nav-pricing")}
        </nav>
        <div className="flex items-center gap-4">
          <button data-testid="mkt-nav-signin" onClick={() => navigate("/login")}
            className="text-[13px] font-medium text-[#1d1d1f]/70 hover:text-[#1d1d1f] transition-colors">{s("nav_signin", t("landing.sign_in", "Sign in"))}</button>
          <Link to="/pricing" data-testid="mkt-nav-getstarted"
            className="demo-pill bg-[#0071E3] hover:bg-[#0077ED] text-white text-[13px] font-semibold px-5 py-2">{s("nav_getstarted", t("landing.get_started", "Get Started"))}</Link>
        </div>
      </div>
      <nav className="md:hidden flex items-center justify-center gap-8 text-[13px] text-[#1d1d1f]/70 pb-2.5 -mt-1" data-testid="mkt-nav-mobile">
        {link("/features", s("nav_features", "Features"), "mkt-nav-features-mobile")}
        {link("/pricing", s("nav_pricing", "Pricing"), "mkt-nav-pricing-mobile")}
      </nav>
    </header>
  );
};

export const MarketingFooter = () => {
  const navigate = useNavigate();
  const { s, brand } = useSite("shell");
  const bn = brand.app_name || "Agentic Inbox";
  return (
    <footer className="bg-[#1d1d1f] border-t border-white/10 pt-16 pb-10 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-10 pb-12">
          <div className="col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-[#0071E3] flex items-center justify-center text-white overflow-hidden">
                {brand.logo_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${brand.logo_url}`} alt={bn} className="w-7 h-7 object-cover" data-testid="mkt-footer-logo" /> : <Mail size={14} />}
              </div>
              <span className="demo-heading font-semibold text-[15px] tracking-tight text-[#F5F5F7]">{bn}</span>
            </div>
            <p className="text-[13px] text-[#86868B] leading-relaxed max-w-xs">
              {s("footer_tagline", "The AI email agent for business owners and solopreneurs — in your inbox, your calendar and your WhatsApp.")}
            </p>
          </div>
          <div>
            <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#86868B] mb-4">{s("footer_product", "Product")}</p>
            <ul className="space-y-2.5 text-[13px] text-[#F5F5F7]/70">
              <li><Link to="/features" className="hover:text-white transition-colors">{s("nav_features", "Features")}</Link></li>
              <li><Link to="/pricing" className="hover:text-white transition-colors">{s("nav_pricing", "Pricing")}</Link></li>
              <li><button data-testid="mkt-footer-signin" onClick={() => navigate("/login")} className="hover:text-white transition-colors">{s("nav_signin", t("landing.sign_in", "Sign in"))}</button></li>
              <li><button data-testid="mkt-footer-distributor" onClick={() => navigate("/become-a-distributor")} className="hover:text-white transition-colors">{s("footer_distributor", "Become a distributor")}</button></li>
            </ul>
          </div>
          <div>
            <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#86868B] mb-4">{s("footer_languages", "Languages")}</p>
            <ul className="space-y-2.5 text-[13px] text-[#F5F5F7]/70">
              <li>English</li><li>Español</li><li>Português</li><li>Français</li>
            </ul>
          </div>
          <div>
            <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#86868B] mb-4">{s("footer_legal", "Legal")}</p>
            <ul className="space-y-2.5 text-[13px] text-[#F5F5F7]/70">
              <li><a data-testid="mkt-footer-privacy" href="/privacy-policy" target="_blank" rel="noreferrer" className="hover:text-white transition-colors">{s("footer_privacy", "Privacy Policy")}</a></li>
              <li><a data-testid="mkt-footer-terms" href="/terms" target="_blank" rel="noreferrer" className="hover:text-white transition-colors">{s("footer_terms", "Terms & Conditions")}</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-[12px] text-[#86868B]">
          <p>{s("footer_copyright", "© 2026 Agentic Inbox. All rights reserved.")}</p>
          <p>{s("footer_meta", "Gmail sync · WhatsApp agent · Team seats")}</p>
        </div>
      </div>
    </footer>
  );
};
